<template>
	<div class="row centered">
		<AppTable ref="self-invoices-table"
			v-if="userStore.currBusinessId"
			:fetch-function="administrationStore.getFilteredSelfInvoices"
			:filters="filters"
			:table-headings="tableHeadings"
			:title="getString('self_invoices')"
			:total-items="administrationStore.selfInvoicesTotal || 0"
			:items="administrationStore.selfInvoices || []"
			initial-sort-field="date"
			:initial-sort-asc="false"
			allow-download
			@export="exportSelfInvoices"
			@failed-fetch="details=>{$emit('failed-fetch', details)}" >

			<template #header-extra-actions>
				<AppButtonIcon prevent-change color="var(--color-bg-primary-1)" icon-name="icon-plus" :title="getString('manual_self_invoice_new')" @click="manualSelfInvoiceOpen = true" />
				<slot name="header-extra-actions" />
			</template>

			<template #reference="{item}">
				<div class="reference">
					<p class="reference-numero">{{ item.reference_numero || '-' }}</p>
					<p v-if="item.reference_date" class="reference-date">{{ readableReferenceDate(item) }}</p>
				</div>
			</template>

			<template #actions="{item}">
				<div class="row align-center centered">
					<AppTableButtonGroup v-if="isViewable(item)" :buttons="viewButtons(item)" :item="item" @picked-view="openUrl(item.url)" />
					<AppTableButtonGroup v-else-if="isRetryable(item)" :buttons="retryButtons(item)" :item="item" @picked-retry="retry(item.self_invoice_id)" />
					<div class="no-download" v-else>N/A</div>
				</div>
			</template>

		</AppTable>
		<ManualSelfInvoiceBottomSheet :active="manualSelfInvoiceOpen" @toggling-popup="closeManualSelfInvoice" @emitted="refresh" />
	</div>
</template>

<script>
import API from "@/shared/helpers/API.js";
import AppTable from "@/shared/components/AppTable.vue";
import AppTableButtonGroup from "@/shared/components/AppTableButtonGroup.vue";
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import ManualSelfInvoiceBottomSheet from "@/console-new/components/config/administration/ManualSelfInvoiceBottomSheet.vue";
import {useUserStore, useAdministrationStore} from "@/console-new/stores";
import {useCurrencyStore, useLanguageStore, useNotificationStore} from "@/shared/stores";
import {doubleFigure, formatDateYYMMDD} from "@/shared/helpers/dates";
import {exportToFile} from "@/shared/helpers/excel";

const STATE_STRINGS = {
	pending: 'self_invoice_generating',
	sent: 'self_invoice_sent',
	confirmed: 'self_invoice_confirmed',
	refused: 'self_invoice_refused',
	failed: 'self_invoice_failed',
};

export default {
	setup() {
		const userStore = useUserStore();
		const administrationStore = useAdministrationStore();
		const languageStore = useLanguageStore();
		const currencyStore = useCurrencyStore();
		const notificationStore = useNotificationStore();
		return {userStore, administrationStore, getString: languageStore.getString, currencyStore, showNotification: notificationStore.showNotification};
	},
	name: "SelfInvoicesTable",
	emits: ["failed-fetch"],
	components: {AppTable, AppTableButtonGroup, AppButtonIcon, ManualSelfInvoiceBottomSheet},
	data() {
		let states = Object.keys(STATE_STRINGS);
		return {
			retryingSelfInvoiceId: null,
			manualSelfInvoiceOpen: false,
			filters: [
				{
					type: "date",
					key: 'date',
					tableName: 'cards_self_invoices',
					text: this.getString('date'),
					finalValue: '',
					active: false
				},
				{
					type: "checklist",
					key: "state",
					text: this.getString('state'),
					values: states.map(state => this.getString(STATE_STRINGS[state])),
					keysArray: states,
					active: false,
					selectedIndexes: [],
					finalValue: '',
				},
			],
			tableHeadings: [
				{
					text: this.getString('date'),
					cols: 1,
					sortable: true,
					active: true,
					sortAsc: false,
					fieldName: 'date',
					formatter: val => val ? doubleFigure(val.getDate()) + '-' + doubleFigure(val.getMonth() + 1) + '-' + val.getFullYear() : '-',
				},
				{
					text: this.getString('number'),
					cols: 1,
					fieldName: 'numero',
					formatter: val => val || '-',
				},
				{
					text: this.getString('self_invoice_reference'),
					cols: 2,
					fieldName: 'reference',
					slotName: 'reference',
				},
				{
					text: this.getString('taxable_amount'),
					cols: 1,
					sortable: true,
					fieldName: 'amount',
					formatter: 'currency',
				},
				{
					text: this.getString('state'),
					cols: 1,
					fieldName: 'state',
					formatter: val => this.stateText(val),
				},
				{
					text: this.getString('self_invoice_source'),
					cols: 1,
					fieldName: 'source',
					formatter: val => this.sourceText(val),
				},
				{
					text: this.getString('actions'),
					cols: 1,
					fieldName: 'actions',
					slotName: 'actions',
				},
			],
		}
	},
	methods: {
		refresh() {
			if(this.$refs['self-invoices-table']) {
				this.$refs['self-invoices-table'].fetchData();
			}
		},
		closeManualSelfInvoice(isOpen) {
			if(!isOpen) {
				this.manualSelfInvoiceOpen = false;
			}
		},
		stateText(state) {
			return STATE_STRINGS[state] ? this.getString(STATE_STRINGS[state]) : '-';
		},
		sourceText(source) {
			return source ? this.getString('self_invoice_source_' + source) : '-';
		},
		readableReferenceDate(item) {
			return item.reference_date ? item.reference_date.split('-').reverse().join('-') : '';
		},
		referenceText(item) {
			return item.reference_numero ? [item.reference_numero, this.readableReferenceDate(item)].filter(Boolean).join(' · ') : '-';
		},
		isViewable(item) {
			return ['sent', 'confirmed'].includes(item.state) && !!item.url;
		},
		isRetryable(item) {
			return ['failed', 'refused'].includes(item.state);
		},
		viewButtons(item) {
			return [
				{
					iconName: "icon-eye-blue",
					emitName: "picked-view",
					title: [this.getString('self_invoice_short'), item.numero].filter(Boolean).join(" "),
				},
			];
		},
		retryButtons(item) {
			return [
				{
					iconName: "icon-redo-blue",
					emitName: "picked-retry",
					loading: this.retryingSelfInvoiceId === item.self_invoice_id,
					disabled: !!this.retryingSelfInvoiceId,
					title: [this.stateText(item.state), item.error, this.getString('retry')].filter(Boolean).join(" · "),
				},
			];
		},
		openUrl(url) {
			window.open(url, '_blank');
		},
		retry(selfInvoiceId) {
			if(this.retryingSelfInvoiceId) {
				return;
			}
			this.retryingSelfInvoiceId = selfInvoiceId;
			API.init().retrySelfInvoice(this.userStore.currBusinessId, selfInvoiceId, (response) => {
				this.retryingSelfInvoiceId = null;
				if(response && response.result === "OK") {
					this.showNotification("generic-success");
				} else {
					this.showNotification("generic-fail");
				}
				this.refresh();
			});
		},
		exportSelfInvoices(expExtension) {
			let sym = this.currencyStore.currCurrency.symbol;
			let businessNameNorm = this.userStore.currBusiness.name.toLowerCase().replace(/[^a-zA-Z0-9]/g, "");
			let dateStr = new Date().toLocaleDateString('it-IT').replace(/\//g, '-');
			exportToFile({
				columns: [
					{ header: this.getString('date'), value: i => formatDateYYMMDD(i.date) },
					{ header: this.getString('number'), value: i => i.numero || '' },
					{ header: this.getString('self_invoice_reference'), value: i => this.referenceText(i) },
					{ header: this.getString('taxable_amount') + ' ' + sym, value: i => this.currencyStore.dbToActualValue(i.amount), type: 'number', numberFormat: '#,##0.00' },
					{ header: this.getString('state'), value: i => this.stateText(i.state) },
					{ header: this.getString('self_invoice_source'), value: i => this.sourceText(i.source) },
				],
				rows: this.administrationStore.selfInvoices || [],
				fileName: businessNameNorm + "_self_invoices_" + dateStr,
				sheetName: this.getString('self_invoices'),
				format: expExtension,
				onEmpty: () => this.showNotification('table-no-data'),
				onError: () => this.showNotification('generic-fail'),
			});
		},
	},
	watch: {
		'userStore.currBusinessId': {
			handler(newVal, oldVal) {
				if(newVal && newVal !== oldVal) {
					this.refresh();
				}
			},
		},
	}
}
</script>

<style lang="scss" scoped>
.no-download {
	font-size: 12px;
}

.reference {
	width: 100%;
	text-align: left;

	p {
		font-size: 12px;
	}

	.reference-numero {
		word-break: break-all;
	}

	.reference-date {
		color: var(--color-txt-2);
	}
}
</style>
